export interface AuthRoutingEnv {
  DEV: boolean;
  VITE_API_PORT?: string;
}

export interface BrowserLocationLike {
  hostname: string;
  origin: string;
  pathname: string;
  search: string;
  hash: string;
}

const DEFAULT_API_PORT = '3090';

function getDefaultAuthEnv(): AuthRoutingEnv {
  return {
    DEV: import.meta.env.DEV,
    VITE_API_PORT: import.meta.env.VITE_API_PORT as string | undefined,
  };
}

function getDefaultLocation(): BrowserLocationLike {
  if (typeof window === 'undefined') {
    return {
      hostname: 'localhost',
      origin: 'http://localhost',
      pathname: '/chat',
      search: '',
      hash: '',
    };
  }

  return window.location;
}

export function getBackendOrigin(
  env: AuthRoutingEnv = getDefaultAuthEnv(),
  location: BrowserLocationLike = getDefaultLocation()
): string {
  if (!env.DEV) {
    return '';
  }

  return `http://${location.hostname}:${env.VITE_API_PORT ?? DEFAULT_API_PORT}`;
}

export function getCurrentAppPath(location: BrowserLocationLike = getDefaultLocation()): string {
  const path = `${location.pathname}${location.search}${location.hash}`;
  return path || '/chat';
}

export function buildBackendUrl(
  path: string,
  env: AuthRoutingEnv = getDefaultAuthEnv(),
  location: BrowserLocationLike = getDefaultLocation()
): string {
  return `${getBackendOrigin(env, location)}${path}`;
}

export function buildArtifactUrl(
  runId: string,
  filename: string,
  env: AuthRoutingEnv = getDefaultAuthEnv(),
  location: BrowserLocationLike = getDefaultLocation()
): string {
  const encodedFilename = filename.split('/').map(encodeURIComponent).join('/');
  return buildBackendUrl(
    `/api/workflows/runs/${encodeURIComponent(runId)}/artifacts/${encodedFilename}`,
    env,
    location
  );
}

function buildReturnTo(
  returnTo: string | undefined,
  env: AuthRoutingEnv,
  location: BrowserLocationLike
): string {
  const target = returnTo && returnTo.trim().length > 0 ? returnTo : getCurrentAppPath(location);

  if (env.DEV) {
    return new URL(target, location.origin).toString();
  }

  try {
    const absolute = new URL(target);
    return `${absolute.pathname}${absolute.search}${absolute.hash}` || '/chat';
  } catch {
    return target;
  }
}

export function buildGithubLoginUrl(
  env: AuthRoutingEnv = getDefaultAuthEnv(),
  location: BrowserLocationLike = getDefaultLocation(),
  returnTo?: string
): string {
  const params = new URLSearchParams({
    return_to: buildReturnTo(returnTo, env, location),
  });

  return `${buildBackendUrl('/auth/github/login', env, location)}?${params.toString()}`;
}
